import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cdpAiStore } from '../../../stores/cdpAiStore';
import { consoleAiPreferencesService } from '../../../services/console-ai-preferences.service';
import { TemplateCard } from './TemplateCard';
import { SlotFillForm } from './SlotFillForm';
import type { ConsoleActionView } from '../../../services/console-ai-actions.service';

type Tab = 'all' | 'favorites' | 'recents' | ConsoleActionView['domain'];

const TABS: Tab[] = ['all', 'favorites', 'recents', 'profiles', 'segments', 'events', 'labels', 'lists'];

export const TemplateLibrary = observer(() => {
  const { t } = useTranslation('console-ai');
  const [tab, setTab] = useState<Tab>('all');
  const [search, setSearch] = useState('');
  const [activeSlots, setActiveSlots] = useState<{
    actionId: string;
    promptKey: string;
    slots: string[];
  } | null>(null);

  useEffect(() => {
    if (!cdpAiStore.actionsLoaded && !cdpAiStore.actionsLoading) {
      void cdpAiStore.loadActions();
    }
    if (cdpAiStore.favorites.length === 0 && cdpAiStore.recents.length === 0) {
      void cdpAiStore.loadPreferences();
    }
  }, []);

  if (!cdpAiStore.isLibraryOpen) return null;

  // Filter by tab
  let visible = cdpAiStore.actions.slice();

  if (tab === 'favorites') {
    visible = visible.filter(a => cdpAiStore.favorites.includes(a.id));
  } else if (tab === 'recents') {
    const order = new Map(cdpAiStore.recents.map((r, i) => [r.actionId, i]));
    visible = visible.filter(a => order.has(a.id)).sort((a, b) => order.get(a.id)! - order.get(b.id)!);
  } else if (tab !== 'all') {
    visible = visible.filter(a => a.domain === tab);
  }

  // Filter by search
  if (search) {
    const s = search.toLowerCase();
    visible = visible.filter(
      a => a.id.toLowerCase().includes(s) || t(a.i18nKey).toLowerCase().includes(s),
    );
  }

  const handleCardSelect = (action: ConsoleActionView, promptKey: string, slots: string[] | undefined) => {
    if (slots && slots.length > 0) {
      setActiveSlots({ actionId: action.id, promptKey, slots });
      return;
    }
    cdpAiStore.setDraft(t(promptKey));
    cdpAiStore.closeLibrary();
    void consoleAiPreferencesService.recordRecent(action.id).catch(() => {});
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('templates.tabs.all', 'Templates')}
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center"
      onClick={() => cdpAiStore.closeLibrary()}
    >
      <div
        className="bg-slate-900 text-white rounded-lg w-[800px] max-h-[80vh] flex overflow-hidden shadow-2xl border border-white/10"
        onClick={e => e.stopPropagation()}
      >
        {/* Sidebar */}
        <aside className="w-44 border-e border-white/10 p-3 text-sm shrink-0 flex flex-col gap-0.5">
          {TABS.map(k => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`block w-full text-start px-2 py-1.5 rounded capitalize transition-colors ${
                tab === k ? 'bg-sky-500/15 text-sky-300' : 'text-slate-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              {t(`templates.tabs.${k}`, k)}
            </button>
          ))}
        </aside>

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Search bar */}
          <div className="p-3 border-b border-white/10">
            <input
              placeholder={t('templates.searchPlaceholder', 'Search templates...')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500"
              autoFocus
            />
          </div>

          {/* Card grid */}
          <div className="overflow-y-auto p-3 flex-1">
            {cdpAiStore.actionsLoading ? (
              <div className="text-sm opacity-60 text-center py-8">{t('panel.loading', 'Loading...')}</div>
            ) : visible.length === 0 ? (
              <div className="text-sm opacity-60 text-center py-8">{t('templates.empty', 'No templates match')}</div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {visible.map(a => (
                  <TemplateCard
                    key={a.id}
                    action={a}
                    isFavorite={cdpAiStore.favorites.includes(a.id)}
                    onSelect={(promptKey, slots) => handleCardSelect(a, promptKey, slots)}
                    onToggleFavorite={() => void cdpAiStore.toggleFavorite(a.id).catch(() => {})}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Slot fill form overlay */}
          {activeSlots && (
            <SlotFillForm
              actionId={activeSlots.actionId}
              slots={activeSlots.slots}
              promptKey={activeSlots.promptKey}
              onCancel={() => setActiveSlots(null)}
              onSubmit={(filled) => {
                cdpAiStore.setDraft(filled);
                cdpAiStore.closeLibrary();
                setActiveSlots(null);
                void consoleAiPreferencesService.recordRecent(activeSlots.actionId).catch(() => {});
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
});
